/**
 * Context Selection Store
 * Manages dialog state and coordinates connection/database selection flow
 */

import { isCancelledError } from '@tanstack/react-query';
import { create } from 'zustand';
import type { ContextRequirements, ResolvedContext, ContextResolutionResult } from '../types/contextTypes';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useConnectionsUIStore } from '../../connections/store/uiStore';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { readDatabaseListingThroughCache } from '../../database-objects/hooks/useDatabaseQueries';
import { syncExplorerSelection } from '../services/explorerSyncService';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

type SelectionPhase = 'idle' | 'connection' | 'database';

type PromiseResolver = {
  resolve: (value: ContextResolutionResult) => void;
};

type ContextSelectionState = {
  // Dialog visibility
  showConnectionSelector: boolean;
  showDatabaseSelector: boolean;
  // Current phase and pending action info
  phase: SelectionPhase;
  pendingAction: string | null;
  // Partial context during selection flow
  targetConnectionId: string | null;
  targetConnectionName: string | null;
  targetServerHost: string | null;
  targetEngine: string | null;
  // Requirements for current selection
  requirements: ContextRequirements | null;
  // Whether user made a selection via dialog (triggers explorer sync)
  userSelectedViaDialog: boolean;
  // Explorer suggestions (pre-selected in dialogs, not auto-used)
  suggestedConnectionId: string | null;
  suggestedDatabase: string | null;
  /** Engine filter from requirements — dialog uses this to show only eligible connections */
  engineFilter: ((engine: string | undefined) => boolean) | null;
};

type RequestContextOptions = {
  /** Force showing selector dialogs even when explorer has a valid selection */
  forceShowSelectors?: boolean;
};

type ContextSelectionActions = {
  requestContext: (
    config: ContextRequirements,
    actionName?: string,
    options?: RequestContextOptions
  ) => Promise<ContextResolutionResult>;
  selectConnection: (connectionId: string) => void;
  selectDatabase: (database: string, setAsDefault?: boolean) => void;
  cancelSelection: () => void;
  // Internal: used by selector dialogs
  getState: () => ContextSelectionState;
};

type ContextSelectionStore = ContextSelectionState & ContextSelectionActions;

// Store resolver outside zustand to support Promise pattern
let currentResolver: PromiseResolver | null = null;

// Transient flag: when set, the next requestContext call will force showing selectors
let forceNextRequest = false;

/** Set flag to force the next requestContext call to show selectors */
export function forceNextContextRequest() {
  forceNextRequest = true;
}

const initialState: ContextSelectionState = {
  showConnectionSelector: false,
  showDatabaseSelector: false,
  phase: 'idle',
  pendingAction: null,
  targetConnectionId: null,
  targetConnectionName: null,
  targetServerHost: null,
  targetEngine: null,
  requirements: null,
  userSelectedViaDialog: false,
  suggestedConnectionId: null,
  suggestedDatabase: null,
  engineFilter: null,
};

export const useContextSelectionStore = create<ContextSelectionStore>(
  (set, get) => ({
    ...initialState,

    requestContext: async (
      config: ContextRequirements,
      actionName?: string,
      options?: RequestContextOptions
    ): Promise<ContextResolutionResult> => {
      const forceSelectors = options?.forceShowSelectors || forceNextRequest;
      if (forceNextRequest) forceNextRequest = false;

      // Get current state
      const { activeConnections } = useConnectionStore.getState();
      const { selectedConnectionId, selectedDatabase, selectedDatabaseConnectionId } =
        useDatabaseUIStore.getState();

      // If no connection required, return null
      if (!config.requireConnection) {
        return null;
      }

      // Check for active connections (optionally filtered by engine)
      const connectedConnections = activeConnections.filter(
        (conn) => conn.status === 'connected'
          && (!config.engineFilter || config.engineFilter(conn.connection?.engine))
      );

      if (connectedConnections.length === 0) {
        // No active connections - open Connections Dialog
        useConnectionsUIStore.getState().setShowConnectionsDialog(true);
        return null;
      }

      // Try to use currently selected connection
      // IMPORTANT: Only use selectedConnectionId if it exists in connected connections
      // This handles stale selection IDs from disconnected connections
      let resolvedConnection = selectedConnectionId
        ? connectedConnections.find((conn) => conn.id === selectedConnectionId)
        : undefined;

      // If forceShowSelectors, bypass explorer default and always show selector when >1
      if (forceSelectors && connectedConnections.length > 1) {
        return showConnectionSelector(set, config, actionName, resolvedConnection?.id ?? null);
      }

      // If no valid selection, try auto-select or show selector
      if (!resolvedConnection) {
        if (connectedConnections.length === 1) {
          // Auto-select the only connected connection
          resolvedConnection = connectedConnections[0];
        } else {
          // Multiple connections - need user selection
          return showConnectionSelector(set, config, actionName);
        }
      }

      // Build base context - always use the resolved connection's ID (never stale selectedConnectionId)
      const baseContext: ResolvedContext = {
        connectionId: resolvedConnection.id,
        connectionName: safeConnectionName(resolvedConnection.connection),
        serverHost: safeConnectionServer(resolvedConnection.connection),
        engine: resolvedConnection.connection.engine || 'mssql',
      };

      // Safety: verify the resolved connection is still valid in activeConnections
      // This handles edge cases like connection being disconnected during async operations
      const stillValid = useConnectionStore.getState().activeConnections.some(
        (c) => c.id === resolvedConnection.id && c.status === 'connected'
      );
      if (!stillValid) {
        console.warn('[ContextSelectionStore] Resolved connection is no longer valid, retrying...');
        // Connection became invalid, show selector or return null
        if (connectedConnections.length > 1) {
          return showConnectionSelector(set, config, actionName);
        }
        useConnectionsUIStore.getState().setShowConnectionsDialog(true);
        return null;
      }

      // If database not required, return connection context
      if (!config.requireDatabase) {
        return baseContext;
      }

      // Check if we have a valid selected database for this connection
      if (!forceSelectors && selectedDatabase && selectedDatabaseConnectionId === resolvedConnection.id) {
        return {
          ...baseContext,
          database: selectedDatabase,
        };
      }

      // Need to fetch databases to potentially auto-select or show selector
      // (also reached when forceSelectors is true to show database selector)
      const explorerDatabase =
        selectedDatabase && selectedDatabaseConnectionId === resolvedConnection.id
          ? selectedDatabase
          : null;

      try {
        // Through the shared listing slot — see `readDatabaseListingThroughCache`.
        const result = await readDatabaseListingThroughCache(
          resolvedConnection.id,
          baseContext.engine,
        );
        // Slice 5b: on a failed / empty listing `result.databases` holds the
        // engine's hardcoded FALLBACK list, so auto-selecting from it would
        // silently run the action against e.g. `master`. Only an `'ok'` listing
        // may auto-select; every other outcome hands the user the database
        // selector, which renders the *Show details* affordance for the failure.
        const databases = result.state === 'ok' ? result.databases : [];

        if (result.state === 'ok') {
          if (databases.length === 0) {
            return null;
          }

          if (databases.length === 1) {
            // Auto-select the only database
            return {
              ...baseContext,
              database: databases[0],
            };
          }
        }

        // Multiple databases (or a listing we cannot trust) - need user selection
        return showDatabaseSelector(set, baseContext, config, actionName, explorerDatabase);
      } catch (error) {
        // A CANCELLATION is not a failure. `readDatabaseListingThroughCache` reads
        // through the shared slot, and `connectionListingLifecycle` cancels that
        // key at every connect boundary — so a boundary crossed mid-resolution
        // rejects here where the old direct `getDatabases` call never threw.
        // Returning `null` for it would abort the user's action silently; the honest
        // answer is the same one an untrusted listing gets, which is the selector.
        if (isCancelledError(error)) {
          return showDatabaseSelector(set, baseContext, config, actionName, explorerDatabase);
        }
        console.error('[ContextSelectionStore] Failed to fetch databases:', error);
        return null;
      }
    },

    selectConnection: (connectionId: string) => {
      const state = get();
      if (state.phase !== 'connection' || !currentResolver) {
        return;
      }

      const { activeConnections } = useConnectionStore.getState();
      const connection = activeConnections.find((c) => c.id === connectionId);

      if (!connection) {
        // Connection not found - cancel selection
        resolveAndReset(set, null, false);
        return;
      }

      // Mark that user made a selection via dialog
      set({ userSelectedViaDialog: true });

      const baseContext: ResolvedContext = {
        connectionId: connection.id,
        connectionName: safeConnectionName(connection.connection),
        serverHost: safeConnectionServer(connection.connection),
        engine: connection.connection.engine || 'mssql',
      };

      // Check if database is required
      if (!state.requirements?.requireDatabase) {
        // Done - resolve with connection only
        resolveAndReset(set, baseContext, true);
        return;
      }

      // Need database - transition to database selection
      transitionToDatabaseSelection(set, baseContext);
    },

    selectDatabase: async (database: string, setAsDefault?: boolean) => {
      const state = get();
      if (state.phase !== 'database' || !currentResolver) {
        return;
      }

      // Mark that user made a selection via dialog
      set({ userSelectedViaDialog: true });

      // Update connection's default database if requested
      if (setAsDefault && state.targetConnectionId) {
        const { connections, updateConnection } = useConnectionStore.getState();
        const connection = connections.find((c) => c.id === state.targetConnectionId);
        if (connection) {
          const updatedConnection = { ...connection, database };
          await updateConnection(updatedConnection);
        }
      }

      const resolvedContext: ResolvedContext = {
        connectionId: state.targetConnectionId!,
        connectionName: state.targetConnectionName!,
        serverHost: state.targetServerHost!,
        engine: state.targetEngine!,
        database,
      };

      resolveAndReset(set, resolvedContext, true);
    },

    cancelSelection: () => {
      resolveAndReset(set, null, false);
    },

    getState: () => {
      const state = get();
      return {
        showConnectionSelector: state.showConnectionSelector,
        showDatabaseSelector: state.showDatabaseSelector,
        phase: state.phase,
        pendingAction: state.pendingAction,
        targetConnectionId: state.targetConnectionId,
        targetConnectionName: state.targetConnectionName,
        targetServerHost: state.targetServerHost,
        targetEngine: state.targetEngine,
        requirements: state.requirements,
        userSelectedViaDialog: state.userSelectedViaDialog,
        suggestedConnectionId: state.suggestedConnectionId,
        suggestedDatabase: state.suggestedDatabase,
        engineFilter: state.engineFilter,
      };
    },
  })
);

// Helper: Show connection selector and return promise
function showConnectionSelector(
  set: (state: Partial<ContextSelectionState>) => void,
  config: ContextRequirements,
  actionName?: string,
  suggestedConnectionId?: string | null
): Promise<ContextResolutionResult> {
  return new Promise((resolve) => {
    currentResolver = { resolve };
    set({
      showConnectionSelector: true,
      showDatabaseSelector: false,
      phase: 'connection',
      pendingAction: actionName || null,
      requirements: config,
      targetConnectionId: null,
      targetConnectionName: null,
      targetServerHost: null,
      targetEngine: null,
      suggestedConnectionId: suggestedConnectionId ?? null,
      engineFilter: config.engineFilter ?? null,
    });
  });
}

// Helper: Show database selector and return promise
function showDatabaseSelector(
  set: (state: Partial<ContextSelectionState>) => void,
  baseContext: ResolvedContext,
  config: ContextRequirements,
  actionName?: string,
  suggestedDatabase?: string | null
): Promise<ContextResolutionResult> {
  return new Promise((resolve) => {
    currentResolver = { resolve };
    set({
      showConnectionSelector: false,
      showDatabaseSelector: true,
      phase: 'database',
      pendingAction: actionName || null,
      requirements: config,
      targetConnectionId: baseContext.connectionId,
      targetConnectionName: baseContext.connectionName,
      targetServerHost: baseContext.serverHost,
      targetEngine: baseContext.engine,
      suggestedDatabase: suggestedDatabase ?? null,
    });
  });
}

// Helper: Transition from connection to database selection
async function transitionToDatabaseSelection(
  set: (state: Partial<ContextSelectionState>) => void,
  baseContext: ResolvedContext
): Promise<void> {
  // Determine explorer's suggested database (only if user picked the same connection as explorer)
  const { selectedDatabase, selectedDatabaseConnectionId } = useDatabaseUIStore.getState();
  const explorerDatabase =
    selectedDatabase && selectedDatabaseConnectionId === baseContext.connectionId
      ? selectedDatabase
      : null;

  // First, try auto-selection if only one database
  try {
    const result = await readDatabaseListingThroughCache(
      baseContext.connectionId,
      baseContext.engine,
    );
    // Same Slice 5b trust rule as `resolveContext` above: never auto-select out
    // of the engine fallback list a failed listing returns.
    const databases = result.state === 'ok' ? result.databases : [];

    if (result.state === 'ok') {
      if (databases.length === 0) {
        resolveAndReset(set, null, false);
        return;
      }

      if (databases.length === 1) {
        // Auto-select the only database - user already selected connection, so sync
        resolveAndReset(set, {
          ...baseContext,
          database: databases[0],
        }, true);
        return;
      }
    }

    // Multiple databases (or an untrusted listing) - show selector with explorer suggestion
    set({
      showConnectionSelector: false,
      showDatabaseSelector: true,
      phase: 'database',
      targetConnectionId: baseContext.connectionId,
      targetConnectionName: baseContext.connectionName,
      targetServerHost: baseContext.serverHost,
      targetEngine: baseContext.engine,
      suggestedDatabase: explorerDatabase,
    });
  } catch (error) {
    console.error('[ContextSelectionStore] Failed to fetch databases:', error);
    resolveAndReset(set, null, false);
  }
}

// Helper: Resolve promise and reset state
function resolveAndReset(
  set: (state: Partial<ContextSelectionState>) => void,
  result: ContextResolutionResult,
  shouldSyncExplorer: boolean = false
): void {
  const resolver = currentResolver;
  currentResolver = null;
  set(initialState);

  // Sync explorer selection if user made a selection via dialog
  if (shouldSyncExplorer && result) {
    syncExplorerSelection({ context: result, skipIfAlreadySelected: false });
  }

  resolver?.resolve(result);
}
